import { useMemo, useState } from "react";
import Deferred from "../../common/utilities/Deferred";

export type DialogProps<TInitValues, TResult = BaseDialogResult> = {
    open: boolean;
    onClose: (result: TResult) => void;
    values?: TInitValues | undefined;
};

export type BaseDialogResultReason = "ok" | "cancel";

export type BaseDialogResult<TReason = BaseDialogResultReason> = {
    reason: TReason;
};

export type DialogResult<TResultValues, TReason = BaseDialogResultReason> = {
    values?: TResultValues | undefined;
} & BaseDialogResult<TReason>;

export type DialogState<TInitValues, TResult = BaseDialogResult> = {
    open: (values?: TInitValues) => Promise<TResult>;
    onClose: (result: TResult) => void;
    isOpen: boolean;
    values: TInitValues | undefined;
};

type InternalState<TInitValues, TResult> = {
    isOpen: boolean;
    values: TInitValues | undefined;
    deferred: Deferred<TResult> | undefined;
};

function createDialogState<TInitValues, TResult>(
    { state, setState }: {
        state: InternalState<TInitValues, TResult>,
        setState: React.Dispatch<React.SetStateAction<InternalState<TInitValues, TResult>>>;
    }
): DialogState<TInitValues, TResult> {

    const onOpen = (values?: TInitValues) => {
        const deferred = new Deferred<TResult>();
        setState({ isOpen: true, values: values, deferred: deferred });
        return deferred.promise;
    };

    const onClose = (result: TResult) => {
        const deferred = state.deferred;
        setState({ isOpen: false, deferred: undefined, values: undefined });
        deferred?.resolve(result);
    };

    return {
        open: onOpen,
        onClose: onClose,
        isOpen: state.isOpen,
        values: state.values
    };
}

export function useDialogState<TInitValues, TResult = BaseDialogResult>(): DialogState<TInitValues, TResult> {
    const [state, setState] = useState<InternalState<TInitValues, TResult>>({ isOpen: false, values: undefined, deferred: undefined });

    return useMemo(() => createDialogState<TInitValues, TResult>({ state, setState }), [state]);
}

export type BindDialogReturn<TInitValues, TResult = BaseDialogResult> = {
    open: boolean,
    onClose: (result: TResult) => void,
    values: TInitValues | undefined;
};

export function bindDialog<TInitValues, TResult = BaseDialogResult>(
    { isOpen, onClose, values }: DialogState<TInitValues, TResult>): BindDialogReturn<TInitValues, TResult> {

    return {
        open: isOpen,
        onClose: onClose,
        values: values
    };
}
